import { uniq } from 'lodash';
import React from 'react';

import UsersClient from 'src/clients/UsersClient';
import { RouteContext, SessionData } from 'src/context';
import { useAppDispatch } from 'src/hooks/useStore';
import { AuthenticatedPortalDataType } from 'src/legacy/components/Onboarding/Login/onboardingLoginTypes';
import { setProcessingCallback } from 'src/store/auth/slice';
import { ensureApiError } from 'src/utils/Errors';

type getWorkspacesOptions = {
  querySession?: boolean;
};

export const useWorkspaces = () => {
  const [loadingWorkspaces, setLoadingWorkspaces] = React.useState(false);
  const { sessionData } = React.useContext(RouteContext);

  const dispatch = useAppDispatch();

  const getWorkspaces = async ({ querySession }: getWorkspacesOptions = {}) => {
    let portalIdToSession: SessionData['portalIdToSession'] = {};
    try {
      setLoadingWorkspaces(true);
      // get valid portal sessions
      if (querySession) {
        // if useApi is true, call the API to get the sessions
        const portalSessionsResponse = await UsersClient.getSessions();
        ({ portalIdToSession } = portalSessionsResponse.data);
      } else {
        // otherwise, use the session data from the context
        ({ portalIdToSession } = sessionData);
      }

      // extract the unique list of emails from the portal sessions
      const userEmails = uniq(
        Object.values(portalIdToSession || {})
          .map((session) => session.email || '') // use || to satisfy typescript
          .filter(Boolean),
      );

      // call GET /workspaces to get the workspaces
      let workspaces: Record<
        string,
        Record<string, AuthenticatedPortalDataType>
      > = await UsersClient.getWorkspaces(userEmails);

      // Filter out empty/invalid user objects
      // Backend may return empty objects for users without workspaces
      workspaces = Object.fromEntries(
        Object.entries(workspaces).filter(
          ([email, portals]) =>
            email && portals && Object.keys(portals).length > 0,
        ),
      );

      setLoadingWorkspaces(false);

      return { workspaces, portalIdToSession, userNotFound: false };
    } catch (ex) {
      const error = ensureApiError(ex);
      // a not_found error means the user exists in Cognito but has no DynamoDB record.
      // This is a valid state for new Google SSO users who haven't completed onboarding.
      // Let the caller decide how to handle it (e.g. redirect to signup/onboarding).
      if (error.code === 'not_found') {
        dispatch(setProcessingCallback(false));
        setLoadingWorkspaces(false);
        return { workspaces: {}, portalIdToSession, userNotFound: true };
      }

      setLoadingWorkspaces(false);
    }
    return {
      workspaces: {},
      portalIdToSession: {},
      userNotFound: false,
    };
  };

  return { getWorkspaces, loadingWorkspaces, setLoadingWorkspaces };
};
